import { includes, map } from 'lodash';
import type { Dispatch } from 'react';
import { useEffect, useState } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import MemberRequestModal from 'hb-react/shared/apps/TopicsIndexV2/components/TopicMemberRequest/MemberRequestModal';
import type {
  ITopic,
  ITopicApproval,
} from 'hb-react/shared/apps/TopicsIndexV2/types';
import handleDisplayErrorToast from 'hb-react/shared/apps/TopicsIndexV2/utils/handleDisplayErrorToast';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

export interface RequestGroupModalProps {
  href: string;
  requestApproval: {
    id: number | null;
  };
  setDisplayRequestModal: Dispatch<boolean>;
  setRequestApproval: Dispatch<{
    id: number | null;
  }>;
  setTopics: Dispatch<ITopic[]>;
  target?: string;
  topic: ITopic;
  topics: ITopic[];
}

const RequestGroupModal = ({
  href,
  requestApproval,
  setDisplayRequestModal,
  setRequestApproval,
  setTopics,
  target,
  topic,
  topics,
}: RequestGroupModalProps) => {
  const [isCanceling, setIsCanceling] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onSuccess = (approval: ITopicApproval) => {
    const newTopics: ITopic[] = map(topics, (oldTopic) => {
      if (oldTopic.id === topic.id) {
        if (approval) {
          return {
            ...oldTopic,
            approval_id: approval.id,
            joined_at: includes(approval.auto_approval_user_list_id, topic.id)
              ? new Date()
              : null,
          };
        }
      }

      return oldTopic;
    });

    setTopics(newTopics);
  };

  const {
    custom_attributes_to_fill: customAttributesToFill,
    automatic_validation: automaticValidation,
    is_public_and_requires_user_to_fill_custom_attributes_to_join:
      hasCustomAttributesAndIsPublic,
  } = topic;

  const isOnAutomaticValidationWithCustomAttributes =
    automaticValidation && customAttributesToFill;

  useEffect(() => {
    const { id, approval_id: approvalId } = topic;
    const approvalPath = approvalId
      ? Routes.topics_topic_approval_path(id, approvalId)
      : Routes.new_topics_topic_approval_path(id);

    setIsLoading(true);

    axios
      .get(approvalPath)
      .then((response) => {
        const topicApproval = response.data.approval;

        setRequestApproval(topicApproval);
      })
      .catch((error) => {
        handleDisplayErrorToast(error);
      })
      .finally(() => setIsLoading(false));
  }, [setIsLoading, setRequestApproval, topic]);

  const handleCancel = (displayNotifications = true) => {
    setIsCanceling(true);
    if (requestApproval.id) {
      axios
        .delete(Routes.topics_topic_approval_path(topic.id, requestApproval.id))
        .then(() => {
          setDisplayRequestModal(false);
          setRequestApproval({ id: null });
          const newTopics = map(topics, (oldTopic) => {
            if (oldTopic.id === topic.id) {
              return {
                ...oldTopic,
                approval_id: null,
              };
            }

            return oldTopic;
          });

          setTopics(newTopics);

          displayNotifications && toast.success(__('Successfully canceled'));
        })
        .catch((error) => handleDisplayErrorToast(error))
        .finally(() => setIsCanceling(false));
    } else {
      setDisplayRequestModal(false);
      toast.success(__('Successfully canceled'));
    }
  };

  const handleSubmitRequest = (values) => {
    setIsSubmitting(true);

    if (requestApproval.id) {
      // Edit request
      axios
        .patch<{ approval: ITopicApproval }>(
          Routes.topics_topic_approval_path(topic.id, requestApproval.id),
          values,
        )
        .then(({ data }) => {
          setRequestApproval(data.approval);
          setIsEditing(false);
          setDisplayRequestModal(false);
          onSuccess(data.approval);
          toast.success(__('Request to join has been successfully updated'));
        })
        .catch((error) => handleDisplayErrorToast(error))
        .finally(() => setIsSubmitting(false));
    } else {
      // New request
      axios
        .post<{ approval: ITopicApproval }>(
          Routes.topics_topic_approvals_path(topic.id),
          values,
        )
        .then(({ data }) => {
          const { approval } = data;

          const shouldShowSuccessDirectly =
            !!approval.created_at &&
            !!approval.auto_approval_user_list_id &&
            approval.status === 'approved';

          if (
            hasCustomAttributesAndIsPublic ||
            isOnAutomaticValidationWithCustomAttributes ||
            shouldShowSuccessDirectly
          ) {
            setDisplayRequestModal(false);
          } else {
            setRequestApproval(approval);
          }
          onSuccess(approval);
        })
        .catch((error) => handleDisplayErrorToast(error))
        .finally(() => setIsSubmitting(false));
    }
  };

  const handleEdit = () => {
    setIsEditing((isEditing) => !isEditing);
  };

  return (
    <MemberRequestModal
      forceDefaultLink={!target}
      handleCancel={handleCancel}
      handleEdit={handleEdit}
      handleSubmitRequest={handleSubmitRequest}
      href={href}
      isCanceling={isCanceling}
      isEditing={isEditing}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      requestApproval={requestApproval}
      setDisplayRequestModal={setDisplayRequestModal}
      target={target}
      topic={topic}
    />
  );
};

export default RequestGroupModal;
